import React, { useState } from 'react';
import './Contact.css';
import { sendPerkProviderForm } from '../services/api';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    phone: ''
  });
  const [formStatus, setFormStatus] = useState(null); // null, 'sending', 'success', 'error'

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    
    try {
      // Send contact form data using the existing API
      await sendPerkProviderForm({
        ...formData,
        type: 'contact_form',
        source: 'website_contact_page'
      });
      
      // Track contact form submission as lead conversion
      if (typeof window.gtag !== 'undefined') {
        // Get UTM parameters if they exist
        const urlParams = new URLSearchParams(window.location.search);
        const utmData = {
          utm_source: urlParams.get('utm_source'),
          utm_medium: urlParams.get('utm_medium'),
          utm_campaign: urlParams.get('utm_campaign')
        };

        // Track as lead generation event
        window.gtag('event', 'generate_lead', {
          event_category: 'engagement',
          event_label: 'Contact Form',
          value: 1,
          currency: 'USD',
          custom_parameters: {
            source: utmData.utm_source || 'direct',
            medium: utmData.utm_medium || 'none',
            campaign: utmData.utm_campaign || 'none'
          }
        });

        // Track as conversion (use a different conversion action for contact form)
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17465397731/POpFCI6RsYUbEOOjk4hB',
          'value': 1.0,
          'currency': 'USD',
          'custom_parameters': {
            'form_type': 'contact_form',
            'source': utmData.utm_source || 'direct'
          }
        });
      }
      
      setFormStatus('success');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        phone: ''
      });
      
      // Reset status after 5 seconds
      setTimeout(() => {
        setFormStatus(null);
      }, 5000);
    } catch (error) {
      console.error('Error sending contact form:', error);
      setFormStatus('error');
      
      // Reset status after 5 seconds
      setTimeout(() => {
        setFormStatus(null);
      }, 5000);
    }
  };

  return (
    <div className="page-container contact-page">
      {/* Hero Banner Section */}
      <section className="contact-hero-banner">
        <div className="hero-overlay"></div>
        <div className="hero-image-container">
          <img src="/images/contact-us.jpg" alt="Contact Us" className="hero-image" />
        </div>
        <div className="container">
          <h1>Contact Us</h1>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="contact-content">
        <div className="container">
          <p className="intro-description">
            Have questions or need assistance? We're here to help. Reach out to our team and we'll get back to you as soon as possible (if its working hours, we usually respond within 2-4 hours).
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <h2>Get in touch</h2>
              <div className="contact-detail">
                <div className="contact-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                </div>
                <div className="contact-text">
                  <h3>Phone</h3>
                  <p><a className="contact-phone-link" href="tel:+18887355515">+1 (888) 735-5515</a></p>
                  <p><a className="contact-phone-link" href="tel:+19842347030">+1 (984) 234-7030</a></p>
                </div>
              </div>
              <div className="contact-detail">
                <div className="contact-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                </div>
                <div className="contact-text">
                  <h3>Email</h3>
                  <p>support@rkubeservices.com</p>
                </div>
              </div>
              <div className="contact-detail">
                <div className="contact-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                </div>
                <div className="contact-text">
                  <h3>Office</h3>
                  <p>4030 Wake Forest Rd
                    <br />Raleigh, North Carolina</p>
                </div>
              </div>
              <div className="contact-hours">
                <h3>Office Hours</h3>
                <p>Monday - Friday: 9:00 AM - 5:00 PM EST</p>
                <p>Saturday - Sunday: Closed</p>
              </div>
            </div>
            <div className="contact-form-container">
              <h2>Send us a message</h2>
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    value={formData.name}
                    onChange={handleChange}
                    required 
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    value={formData.email}
                    onChange={handleChange}
                    required 
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="phone" 
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <input 
                    type="text" 
                    id="subject" 
                    name="subject" 
                    value={formData.subject}
                    onChange={handleChange}
                    required 
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea 
                    id="message" 
                    name="message" 
                    rows="5" 
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={formStatus === 'sending'}
                >
                  {formStatus === 'sending' ? 'Sending...' : 'Send Message'}
                </button>
                {formStatus === 'success' && (
                  <div className="form-status success">
                    <p>Your message has been sent successfully!</p>
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="form-status error">
                    <p>There was an error sending your message. Please try again.</p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>What services does rkube provide?</h3>
              <p>
                We provide a comprehensive suite of financial and operational services including bookkeeping, 
                tax preparation, people and payroll management, equity management, compliance support, and 
                company setup assistance.
              </p>
            </div>
            <div className="faq-item">
              <h3>How quickly can I expect a response from your team?</h3>
              <p>
                We strive to respond to all inquiries within 24 business hours. For urgent matters, 
                please indicate so in your message subject line.
              </p>
            </div>
            <div className="faq-item">
              <h3>Do you offer services for international businesses?</h3>
              <p>
                Yes, we work with businesses around the world that have U.S. operations or 
                need assistance with U.S. financial and tax compliance.
              </p>
            </div>
            <div className="faq-item">
              <h3>How do I get started with your services?</h3>
              <p>
                You can get started by filling out the contact form above, or sign up a trial for one of our services 
                through our website. Our team will reach out to discuss your specific needs and 
                recommend the best approach.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact; 